import React, { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useEventParticipationSubmission } from "../../context/EventParticipationSubmissionManager";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

function EventParticipationFieldsForm() {
    const { eventId } = useParams();
    const history = useHistory();
    const { formData, updateCustomField, submitForm, errors, customFields, loading } = useEventParticipationSubmission();
    const [submitting, setSubmitting] = useState(false);
    const [localErrors, setLocalErrors] = useState({});

    const validateForm = useCallback(() => {
        const newErrors = {};

        customFields.forEach(field => {
            if (field.required && field.enabled) {
                const value = formData.customFields[field.id];

                if (field.fieldType === "checkbox" && !value) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                } else if (field.fieldType === "number" && (value === null || value === undefined || value === "")) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                } else if (["text", "textarea", "select"].includes(field.fieldType) && (!value || value.trim() === "")) {
                    newErrors[field.id] = `${field.fieldName} is verplicht`;
                }
            }
        });

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [customFields, formData.customFields]);


    const handleSubmit = async (event) => {
        event.preventDefault();

        if (validateForm()) {
            setSubmitting(true);

            try {
                const success = await submitForm();

                if (success) {
                    history.push(`/event/${eventId}/participation-submission/success`);
                }
            } catch (error) {
                console.error("Error submitting form:", error);
                setLocalErrors({
                    submit: "Er is een fout opgetreden bij het versturen van de aanvullende informatie. Neem contact op met de organisatie."
                });
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleChange = (fieldId, value) => {
        updateCustomField(fieldId, value);

        // Clear error when user interacts with field
        if (localErrors[fieldId]) {
            setLocalErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldId];
                return newErrors;
            });
        }
    };

    const renderField = (field) => {
        if (!field.enabled) return null;

        const fieldValue = formData.customFields[field.id] || "";
        const hasError = !!localErrors[field.id];

        switch (field.fieldType) {
        case "text":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <input
                        type="text"
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "textarea":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <textarea
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                        rows={3}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "number":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <input
                        type="number"
                        className={`form-control ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    />
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        case "select":
            return (
                <div className="mb-3" key={field.id}>
                    <label htmlFor={`field-${field.id}`} className="form-label">
                        {field.fieldName}{field.required ? " *" : ""}
                    </label>
                    {field.explanation && (
                        <OverlayTrigger overlay={(props) => (
                            <Tooltip {...props}>
                                {field.explanation}
                            </Tooltip>
                        )}>
                            <i className="fa-solid fa-circle-info ml-2" style={{ cursor: "pointer" }} />
                        </OverlayTrigger>
                    )}
                    <select
                        className={`form-control form-select ${hasError ? "is-invalid" : ""}`}
                        id={`field-${field.id}`}
                        value={fieldValue}
                        onChange={(event) => handleChange(field.id, event.target.value)}
                    >
                        <option value="">Selecteer...</option>
                        {field.attributes && field.attributes.options && field.attributes.options.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                    {hasError && (
                        <div className="invalid-feedback">{localErrors[field.id]}</div>
                    )}
                </div>
            );

        default:
            return null;
        }
    };

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="mb-0">Aanvullende informatie</h3>
            </div>
            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    {loading ? (
                        <div className="text-center">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Laden...</span>
                            </div>
                            <p>Aanvullende vragen laden...</p>
                        </div>
                    ) : (
                        <>
                            {customFields && customFields.length === 0 ? (
                                <div className="alert alert-info">
                                        Er zijn geen aanvullende vragen voor dit evenement.
                                </div>
                            ) : (
                                <div>
                                    <p>Vul de onderstaande informatie in om je inschrijving af te ronden.</p>
                                    {customFields && customFields.map(renderField)}
                                </div>
                            )}
                        </>
                    )}

                    {errors.submit && (
                        <div className="alert alert-danger">{errors.submit}</div>
                    )}

                    <div className="d-flex justify-content-between mt-4">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => history.push(`/event/${eventId}/participation-submission/vehicle`)}
                            disabled={submitting}
                        >
                                Terug
                        </button>
                        <button
                            type="submit"
                            className="btn btn-success"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                                    Versturen...
                                </>
                            ) : (
                                "Inschrijving afronden"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EventParticipationFieldsForm;
